import { detectGameType } from './gameTypeDetect.js';
import { areCategoriesCompatible } from './playerCategorizationUtils.js';
import { debugLog, MATCHMAKING_CONFIG, RELAXATION_LEVELS } from './matchmakingConfig.js';
import { canPartnershipPlay, wouldBeFairAfterGame, wouldReachMinPlus2 } from './poolBuilder.js';
import { isTeamEffectivelyAllMale, isTeamPhysicallyMixed, getLevelsEloForMatchmaking, isSessionMensDoublesEloExemptLineup, isMensDoublesEligible } from './competitiveGender.js';
import { 
  scoreCombinationNormalized, 
  compareScoringSystems,
  shouldUseNormalizedScoring,
  shouldCompareScoringSystems,
  getScoringConfig,
} from './scoringIntegration.js';

function normalizeGender(g) {
  if (g == null || g === '') return null;
  return String(g).trim().toLowerCase();
}

function isMixed(team) {
  const genders = team.map((p) => normalizeGender(p?.gender)).filter(Boolean);
  return genders.includes('male') && genders.includes('female');
}

function isAllGender(team, gender) {
  const want = normalizeGender(gender);
  if (!want) return false;
  const players = team.filter(Boolean);
  if (players.length === 0) return false;
  return players.every((p) => normalizeGender(p.gender) === want);
}

function isUnbalancedGameType(team1, team2) {
  return (
    (isAllGender(team1, 'male') && isMixed(team2)) ||
    (isAllGender(team1, 'female') && isMixed(team2)) ||
    (isAllGender(team2, 'male') && isMixed(team1)) ||
    (isAllGender(team2, 'female') && isMixed(team1))
  );
}

function isUnfairGenderMatchup(team1, team2) {
  return (
    (isAllGender(team1, 'female') && isAllGender(team2, 'male')) ||
    (isAllGender(team1, 'male') && isAllGender(team2, 'female'))
  );
}

function isGameTypeAllowed(team1, team2, gameTypeRestriction = 'all_types', playerWaitMetrics = {}) {
  const restriction = gameTypeRestriction || 'all_types';
  if (restriction === 'all_types') return true;
  const team1Mixed = isMixed(team1);
  const team2Mixed = isMixed(team2);
  if (restriction === 'balanced_and_mens_vs_mixed') {
    if (isUnbalancedGameType(team1, team2)) return false;
  }
  if (restriction === 'balanced_only') {
    // Eligible-female teams can form effective-all-male compositions.
    // isTeamEffectivelyAllMale uses physical gender + mensDoublesEligible flag.
    const t1EffAllMale = isTeamEffectivelyAllMale(team1.map(p => p?.id), Object.fromEntries(team1.map(p => [p?.id, p])), playerWaitMetrics);
    const t2EffAllMale = isTeamEffectivelyAllMale(team2.map(p => p?.id), Object.fromEntries(team2.map(p => [p?.id, p])), playerWaitMetrics);

    // Both effectively-all-male → balanced mens doubles
    if (t1EffAllMale && t2EffAllMale) return true;
    // Both physically mixed → balanced mixed
    if (team1Mixed && team2Mixed) return true;
    // Both physically all-female (no eligibility) → balanced womens
    if (isAllGender(team1, 'female') && isAllGender(team2, 'female')) return true;

    debugLog('🚫 isGameTypeAllowed: balanced_only not satisfied', {
      team1Mixed, team2Mixed, t1EffAllMale, t2EffAllMale,
    });
    return false;
  }
  if (restriction === 'mixed_only') {
    return team1Mixed && team2Mixed;
  }
  if (restriction === 'mens_only') {
    return isAllGender(team1, 'male') && isAllGender(team2, 'male');
  }
  if (restriction === 'ladies_only') {
    return isAllGender(team1, 'female') && isAllGender(team2, 'female');
  }
  if (restriction === 'balanced_mens_and_womens') {
    if (isUnfairGenderMatchup(team1, team2)) return false;
    return isAllGender(team1, 'male') || isAllGender(team2, 'female') || isMixed(team1) || isMixed(team2);
  }
  if (restriction === 'balanced_and_mixed_only') {
    if (!team1Mixed || !team2Mixed) return false;
    return !isUnfairGenderMatchup(team1, team2);
  }
  return true;
}

function getUnbalancedGamePenalty(team1, team2, gameTypeRestriction = 'all_types') {
  if (gameTypeRestriction === 'all_types') {
    if (isUnbalancedGameType(team1, team2)) {
      return MATCHMAKING_CONFIG.penalties.unbalancedMixedMismatch;
    }
    if (isUnfairGenderMatchup(team1, team2)) {
      return MATCHMAKING_CONFIG.penalties.unbalancedMensVsLadies;
    }
  }
  return 0;
}

function buildPartnershipMap(partnerships = []) {
  const map = new Map();
  partnerships.forEach(p => {
    const p1 = p.player1Id || p.player1_id;
    const p2 = p.player2Id || p.player2_id;
    map.set(p1, p2);
    map.set(p2, p1);
  });
  return map;
}

/**
 * Check if any player pair in the game exceeds the consecutive interaction threshold
 * Returns true if the game should be blocked
 * @param {Array} team1 - Team 1 player IDs
 * @param {Array} team2 - Team 2 player IDs
 * @param {Object} sessionState - Session state with matchHistory
 * @param {Array} partnerships - Array of partnership objects
 * @returns {Object} { shouldBlock: boolean, maxConsecutive: number, blockingPair: Array }
 */
function checkConsecutiveInteractionBlock(team1, team2, sessionState, partnerships = []) {
  // Check if consecutive interaction blocking is enabled
  if (!MATCHMAKING_CONFIG.consecutiveInteractionBlock.enabled) {
    return { shouldBlock: false, maxConsecutive: 0, blockingPair: null };
  }
  
  const threshold = MATCHMAKING_CONFIG.consecutiveInteractionBlock.threshold;
  const allPlayers = [...team1, ...team2];
  
  // Build partnership map for exemption check
  const partnershipMap = buildPartnershipMap(partnerships);
  
  let maxConsecutive = 0;
  let blockingPair = null;
  
  // Check all player pairs in the game
  for (let i = 0; i < allPlayers.length; i++) {
    for (let j = i + 1; j < allPlayers.length; j++) {
      const player1Id = allPlayers[i];
      const player2Id = allPlayers[j];
      
      // Skip if these players are in a partnership (they're forced together)
      const isPartnership = (partnershipMap.get(player1Id) === player2Id) || 
                           (partnershipMap.get(player2Id) === player1Id);
      if (isPartnership) {
        continue;
      }
      
      // Calculate consecutive interactions for this pair
      const consecutive = calculateConsecutiveInteractions(player1Id, player2Id, sessionState);
      const consecutiveTotal = consecutive.consecutiveTotal;
      
      if (consecutiveTotal > maxConsecutive) {
        maxConsecutive = consecutiveTotal;
        blockingPair = [player1Id, player2Id];
      }
      
      // If this pair exceeds the threshold, block the game
      if (consecutiveTotal >= threshold) {
        return { 
          shouldBlock: true, 
          maxConsecutive: consecutiveTotal,
          blockingPair: [player1Id, player2Id]
        };
      }
    }
  }
  
  return { shouldBlock: false, maxConsecutive, blockingPair };
}

function getSameTeamInteractionCount(player1Id, player2Id, sessionState) {
  if (!sessionState || !sessionState.playerInteractions) return 0;
  const key = [player1Id, player2Id].sort().join('-');
  const interactions = sessionState.playerInteractions || {};
  // Use stored same_team_count from database (faster than calculating)
  const sameTeamKey = `${key}_sameTeam`;
  return interactions[sameTeamKey] || 0;
}

/**
 * Calculate consecutive interactions by looking at the most recent games FOR EACH PLAYER
 * Returns the count of consecutive games where these players have interacted
 * "Consecutive" means consecutive from EACH PLAYER'S perspective (their 1st, 2nd, 3rd game, etc.)
 * @param {string} player1Id - First player ID
 * @param {string} player2Id - Second player ID
 * @param {Object} sessionState - Session state with matchHistory
 * @returns {Object} { consecutiveSameTeam: number, consecutiveTotal: number }
 */
function calculateConsecutiveInteractions(player1Id, player2Id, sessionState) {
  if (!sessionState.matchHistory || !Array.isArray(sessionState.matchHistory)) {
    return { consecutiveSameTeam: 0, consecutiveTotal: 0 };
  }
  
  // Build list of games for each player (in order they played)
  const player1Games = [];
  const player2Games = [];
  
  sessionState.matchHistory.forEach((game, gameIndex) => {
    if (!game) {
      return;
    }
    
    // Support both old format (teams: [team1, team2]) and new format (team1, team2)
    let team1, team2;
    if (game.teams && Array.isArray(game.teams) && game.teams.length >= 2) {
      [team1, team2] = game.teams;
    } else if (game.team1 && game.team2) {
      team1 = game.team1;
      team2 = game.team2;
    } else {
      return;
    }
    
    const team1Ids = new Set(team1.map(p => (typeof p === 'string' ? p : p.id)));
    const team2Ids = new Set(team2.map(p => (typeof p === 'string' ? p : p.id)));
    const allPlayerIds = new Set([...team1Ids, ...team2Ids]);
    
    if (allPlayerIds.has(player1Id)) {
      player1Games.push({ gameIndex, team1Ids, team2Ids, allPlayerIds });
    }
    if (allPlayerIds.has(player2Id)) {
      player2Games.push({ gameIndex, team1Ids, team2Ids, allPlayerIds });
    }
  });
  
  // Find the most recent consecutive games where both players interacted
  // Check from each player's perspective and use the maximum
  const player1Consecutive = countConsecutiveFromPlayerPerspective(player1Id, player2Id, player1Games);
  const player2Consecutive = countConsecutiveFromPlayerPerspective(player2Id, player1Id, player2Games);
  
  // Use the maximum consecutive count from either player's perspective
  return {
    consecutiveSameTeam: Math.max(player1Consecutive.consecutiveSameTeam, player2Consecutive.consecutiveSameTeam),
    consecutiveTotal: Math.max(player1Consecutive.consecutiveTotal, player2Consecutive.consecutiveTotal),
  };
}

/**
 * Count consecutive interactions from a single player's perspective
 * @param {string} playerId - The player whose perspective we're checking
 * @param {string} otherPlayerId - The other player to check interactions with
 * @param {Array} playerGames - Array of games this player participated in (chronological order)
 * @returns {Object} { consecutiveSameTeam: number, consecutiveTotal: number }
 */
function countConsecutiveFromPlayerPerspective(playerId, otherPlayerId, playerGames) {
  let consecutiveSameTeam = 0;
  let consecutiveTotal = 0;
  
  // Process from most recent games backwards (reverse order)
  for (let i = playerGames.length - 1; i >= 0; i--) {
    const game = playerGames[i];
    const { team1Ids, team2Ids, allPlayerIds } = game;
    
    // Check if the other player was in this game
    const otherPlayerInGame = allPlayerIds.has(otherPlayerId);
    
    if (!otherPlayerInGame) {
      // Other player wasn't in this game, stop counting consecutive
      break;
    }
    
    // Other player was in this game, increment consecutive total
    consecutiveTotal++;
    
    // Check if they were on the same team
    const bothOnTeam1 = team1Ids.has(playerId) && team1Ids.has(otherPlayerId);
    const bothOnTeam2 = team2Ids.has(playerId) && team2Ids.has(otherPlayerId);
    const sameTeam = bothOnTeam1 || bothOnTeam2;
    
    if (sameTeam) {
      consecutiveSameTeam++;
    } else {
      // They were opponents in this game, reset same-team consecutive count
      consecutiveSameTeam = 0;
    }
  }
  
  return { consecutiveSameTeam, consecutiveTotal };
}

/**
 * Get consecutive total interaction count (regardless of same-team vs opponent)
 * This counts how many consecutive games two players have interacted (either together or against)
 * @param {string} player1Id - First player ID
 * @param {string} player2Id - Second player ID
 * @param {Object} sessionState - Session state with matchHistory
 * @returns {number} Consecutive interaction count (0, 1, 2, 3+)
 */
/**
 * Calculate penalty for same-team repeats
 * Heavily penalize if players played together on the same team in recent games
 * @param {string} player1Id - First player ID
 * @param {string} player2Id - Second player ID
 * @param {Object} sessionState - Session state with matchHistory
 * @returns {number} Penalty value (higher for immediate same-team repeats)
 */
/**
 * Unified interaction penalty system - percentage-based only
 * Calculates penalties for both same-team and total interactions based on future state
 * Returns object with penalties and shouldBlock flags
 * Does NOT apply to partnerships
 * @param {string} player1Id - First player ID
 * @param {string} player2Id - Second player ID
 * @param {Object} sessionState - Session state with gamesPlayedThisSession, matchHistory, playerInteractions
 * @param {Array} partnerships - Array of partnership objects
 * @param {boolean} isSameTeam - Whether this is a same-team interaction (true) or opponent (false)
 * @returns {Object} { sameTeamPenalty: number, totalPenalty: number, shouldBlock: boolean }
 */
function calculateUnifiedInteractionPenalties(player1Id, player2Id, sessionState, partnerships = [], isSameTeam = false) {
  // Check if these players are in a partnership - if so, no penalty
  const isPartnership = partnerships.some(p => {
    const partner1 = p.player1Id || p.player1_id;
    const partner2 = p.player2Id || p.player2_id;
    return (partner1 === player1Id && partner2 === player2Id) || 
           (partner1 === player2Id && partner2 === player1Id);
  });
  
  if (isPartnership) {
    return { sameTeamPenalty: 0, totalPenalty: 0, shouldBlock: false }; // No penalty for partnerships
  }
  
  const player1Games = sessionState.gamesPlayedThisSession?.[player1Id] || 0;
  const player2Games = sessionState.gamesPlayedThisSession?.[player2Id] || 0;
  
  // Need at least one game played to calculate percentage
  if (player1Games === 0 && player2Games === 0) {
    return { sameTeamPenalty: 0, totalPenalty: 0, shouldBlock: false };
  }
  
  // Use the player with fewer games for percentage calculation (more conservative approach)
  const minGames = Math.min(player1Games, player2Games);
  
  if (minGames === 0) {
    return { sameTeamPenalty: 0, totalPenalty: 0, shouldBlock: false };
  }
  
  // Calculate consecutive interactions (most recent games)
  const consecutive = calculateConsecutiveInteractions(player1Id, player2Id, sessionState);
  
  // Get interaction counts from playerInteractions (stored in database)
  // Format: { "player1-player2": totalCount, "player1-player2_sameTeam": sameTeamCount }
  // Note: totalCount = opponent_count + same_team_count (calculated in buildPlayerInteractions)
  const interactionKey = [player1Id, player2Id].sort().join('-');
  const currentTotalCount = sessionState.playerInteractions?.[interactionKey] || 0; // Total = opponents + teammates
  const sameTeamCount = sessionState.playerInteractions?.[`${interactionKey}_sameTeam`] || 0; // Teammates only
  
  // Calculate FUTURE state: what will the percentages be AFTER this game is played?
  const futureGames = minGames + 1;
  const futureSameTeamCount = isSameTeam ? sameTeamCount + 1 : sameTeamCount;
  const futureTotalCount = currentTotalCount + 1;
  
  // Calculate future consecutive counts (if this game is played)
  const futureConsecutiveSameTeam = isSameTeam ? consecutive.consecutiveSameTeam + 1 : 0;
  const futureConsecutiveTotal = consecutive.consecutiveTotal + 1;
  
  // Calculate future percentages
  const futureSameTeamPercentage = (futureSameTeamCount / futureGames) * 100;
  const futureTotalPercentage = (futureTotalCount / futureGames) * 100;
  
  // Check interaction density: if total interactions >= games - 1, that's way too high
  if (futureTotalCount >= futureGames - 1) {
    return {
      sameTeamPenalty: 0,
      totalPenalty: MATCHMAKING_CONFIG.penalties.interactions.total.densityThreshold,
      shouldBlock: true // Hard block for extreme density
    };
  }
  
  // Calculate same-team penalty using simplified two-threshold system
  let sameTeamPenalty = 0;
  let shouldBlockSameTeam = false;
  const sameTeamLowThreshold = MATCHMAKING_CONFIG.penalties.interactions.sameTeam.lowThreshold;
  const sameTeamHighThreshold = MATCHMAKING_CONFIG.penalties.interactions.sameTeam.highThreshold;
  
  if (futureSameTeamPercentage >= sameTeamHighThreshold) {
    sameTeamPenalty = MATCHMAKING_CONFIG.penalties.interactions.sameTeam.highPenalty;
    shouldBlockSameTeam = true; // Heavy penalty - almost hard block
  } else if (futureSameTeamPercentage >= sameTeamLowThreshold) {
    sameTeamPenalty = MATCHMAKING_CONFIG.penalties.interactions.sameTeam.lowPenalty;
  }
  
  // Apply consecutive same-team penalties (heavily penalize recent consecutive interactions)
  // If players have been on the same team for 2 consecutive games, heavily penalize a 3rd consecutive
  // This addresses the perception that "all their games include this player"
  if (isSameTeam && consecutive.consecutiveSameTeam >= 2) {
    // They've been on the same team for 2 consecutive games, this would be the 3rd
    sameTeamPenalty += MATCHMAKING_CONFIG.penalties.interactions.consecutive.sameTeam3Plus;
    shouldBlockSameTeam = true; // Almost hard block for 3+ consecutive
    debugLog('🚫 Consecutive same-team penalty: 3+ consecutive same-team games (perception deterrent)', {
      player1Id,
      player2Id,
      currentConsecutiveSameTeam: consecutive.consecutiveSameTeam,
      futureConsecutiveSameTeam: futureConsecutiveSameTeam,
      penalty: MATCHMAKING_CONFIG.penalties.interactions.consecutive.sameTeam3Plus,
    });
  } else if (isSameTeam && consecutive.consecutiveSameTeam === 1) {
    // They've been on the same team for 1 consecutive game, this would be the 2nd
    sameTeamPenalty += MATCHMAKING_CONFIG.penalties.interactions.consecutive.sameTeam2;
    debugLog('⚠️ Consecutive same-team penalty: 2 consecutive same-team games', {
      player1Id,
      player2Id,
      currentConsecutiveSameTeam: consecutive.consecutiveSameTeam,
      futureConsecutiveSameTeam: futureConsecutiveSameTeam,
      penalty: MATCHMAKING_CONFIG.penalties.interactions.consecutive.sameTeam2,
    });
  }
  
  // Calculate total interaction penalty using simplified two-threshold system
  let totalPenalty = 0;
  let shouldBlockTotal = false;
  const totalLowThreshold = MATCHMAKING_CONFIG.penalties.interactions.total.lowThreshold;
  const totalHighThreshold = MATCHMAKING_CONFIG.penalties.interactions.total.highThreshold;
  
  if (futureTotalPercentage >= totalHighThreshold) {
    totalPenalty = MATCHMAKING_CONFIG.penalties.interactions.total.highPenalty;
    shouldBlockTotal = true; // Heavy penalty - almost hard block
  } else if (futureTotalPercentage >= totalLowThreshold) {
    totalPenalty = MATCHMAKING_CONFIG.penalties.interactions.total.lowPenalty;
  }
  
  // Apply consecutive total interaction penalties (heavily penalize recent consecutive interactions)
  // If players have interacted (with or against) in 2 consecutive games, heavily penalize a 3rd consecutive
  // This addresses the perception that "all their games include this player"
  if (consecutive.consecutiveTotal >= 2) {
    // They've interacted in 2 consecutive games, this would be the 3rd consecutive interaction
    totalPenalty += MATCHMAKING_CONFIG.penalties.interactions.consecutive.total3Plus;
    shouldBlockTotal = true; // Almost hard block for 3+ consecutive
    debugLog('🚫 Consecutive total interaction penalty: 3+ consecutive total interactions (perception deterrent)', {
      player1Id,
      player2Id,
      currentConsecutiveTotal: consecutive.consecutiveTotal,
      futureConsecutiveTotal: futureConsecutiveTotal,
      penalty: MATCHMAKING_CONFIG.penalties.interactions.consecutive.total3Plus,
    });
  } else if (consecutive.consecutiveTotal === 1) {
    // They've interacted in 1 consecutive game, this would be the 2nd consecutive interaction
    totalPenalty += MATCHMAKING_CONFIG.penalties.interactions.consecutive.total2;
    debugLog('⚠️ Consecutive total interaction penalty: 2 consecutive total interactions', {
      player1Id,
      player2Id,
      currentConsecutiveTotal: consecutive.consecutiveTotal,
      futureConsecutiveTotal: futureConsecutiveTotal,
      penalty: MATCHMAKING_CONFIG.penalties.interactions.consecutive.total2,
    });
  }
  
  return {
    sameTeamPenalty,
    totalPenalty,
    shouldBlock: shouldBlockSameTeam || shouldBlockTotal
  };
}

function scoreGameTypeBalance(team1, team2, playersById) {
  const team1Males = team1.filter(id => playersById[id]?.gender === 'male').length;
  const team2Males = team2.filter(id => playersById[id]?.gender === 'male').length;
  const team1Type = team1Males === 2 ? 'mens' : team1Males === 0 ? 'womens' : 'mixed';
  const team2Type = team2Males === 2 ? 'mens' : team2Males === 0 ? 'womens' : 'mixed';
  if (team1Type === team2Type) {
    return MATCHMAKING_CONFIG.bonuses.balancedGameType;
  }
  const matchup = `${team1Type}_vs_${team2Type}`;
  if (matchup === 'mens_vs_ladies' || matchup === 'ladies_vs_mens') {
    return MATCHMAKING_CONFIG.penalties.unbalancedMensVsLadies;
  }
  return MATCHMAKING_CONFIG.penalties.unbalancedMixedMismatch;
}

function getCategoryKeyForGame(gameTypeId) {
  return gameTypeId === 1 ? 'mixed' : 'levels';
}

function evaluateCategoryMatch(team1 = [], team2 = [], playerCategories = null, fairnessContext = null) {
  if (!playerCategories || Object.keys(playerCategories).length === 0) {
    return { allowed: true, penalty: 0, gap: null, fairnessOverride: false };
  }
  const gameType = detectGameType(team1, team2);
  
  // Determine the correct category key based on game type
  let categoryKey;
  if (gameType.id === 1) {
    // Mixed doubles
    categoryKey = 'mixed';
  } else {
    // Levels game - need to check gender
    // All players should be same gender in levels games
    const allPlayers = [...team1, ...team2];
    const firstPlayerGender = allPlayers[0]?.gender?.toLowerCase();
    
    if (firstPlayerGender === 'female') {
      categoryKey = 'levels_female';
    } else {
      categoryKey = 'levels_male';
    }
  }
  
  const categoryData = playerCategories[categoryKey];
  if (!categoryData) {
    return { allowed: true, penalty: 0, gap: null, fairnessOverride: false };
  }
  // Access the categories map from the categorization result structure
  const categoryMap = categoryData.categories || categoryData;
  if (!categoryMap || Object.keys(categoryMap).length === 0) {
    return { allowed: true, penalty: 0, gap: null, fairnessOverride: false };
  }
  const players = [...team1, ...team2];
  let maxGap = 0;
  let comparisons = 0;
  for (let i = 0; i < players.length; i += 1) {
    const playerA = players[i];
    const categoryA = categoryMap[playerA?.id];
    if (!categoryA) continue;
    for (let j = i + 1; j < players.length; j += 1) {
      const playerB = players[j];
      const categoryB = categoryMap[playerB?.id];
      if (!categoryB) continue;
      const { allowed, gap } = areCategoriesCompatible(categoryA, categoryB);
      if (gap === Infinity) {
        continue;
      }
      comparisons += 1;
      // Track the maximum gap
      if (gap > maxGap) {
        maxGap = gap;
      }
    }
  }
  if (comparisons === 0) {
    return { allowed: true, penalty: 0, gap: null, fairnessOverride: false };
  }
  
  // Get the configurable hard block threshold (default 3)
  const hardBlockThreshold = MATCHMAKING_CONFIG.penalties.categoryGapHardBlock || 3;
  
  // Gap >= hardBlockThreshold is a hard block
  if (maxGap >= hardBlockThreshold) {
    return {
      allowed: false,
      penalty: -Infinity,
      gap: maxGap,
      fairnessOverride: false,
    };
  }
  
  // Apply penalties based on gap size
  let penalty = 0;
  if (maxGap === 1) {
    penalty = -MATCHMAKING_CONFIG.penalties.categoryGap1; // Small penalty for +1 gap
  } else if (maxGap === 2) {
    penalty = -MATCHMAKING_CONFIG.penalties.categoryGap2; // Bigger penalty for +2 gap
  }
  // Gap 0 has no penalty (same group)
  
  return {
    allowed: true,
    penalty,
    gap: maxGap,
    fairnessOverride: false,
  };
}

function scoreELOBalance(team1, team2, playersById) {
  const team1Players = team1.map(id => playersById[id]).filter(Boolean);
  const team2Players = team2.map(id => playersById[id]).filter(Boolean);
  if (team1Players.length !== 2 || team2Players.length !== 2) {
    return 0;
  }
  const team1EloSum = getTeamElo(team1Players);
  const team2EloSum = getTeamElo(team2Players);
  const team1EloAvg = team1EloSum / 2;
  const team2EloAvg = team2EloSum / 2;
  const teamEloDiff = Math.abs(team1EloAvg - team2EloAvg);
  const teamBalance = MATCHMAKING_CONFIG.elo.teamBalanceBase - (teamEloDiff * MATCHMAKING_CONFIG.elo.teamBalanceMultiplier);
  const teamBalanceClamped = Math.max(0, teamBalance);
  const team1IsMixed = isMixed(team1Players);
  const team2IsMixed = isMixed(team2Players);
  const team1Player1Elo = team1IsMixed ? (team1Players[0].mixed_elo_rating || 1200) : (team1Players[0].levels_elo_rating || 1200);
  const team1Player2Elo = team1IsMixed ? (team1Players[1].mixed_elo_rating || 1200) : (team1Players[1].levels_elo_rating || 1200);
  const team2Player1Elo = team2IsMixed ? (team2Players[0].mixed_elo_rating || 1200) : (team2Players[0].levels_elo_rating || 1200);
  const team2Player2Elo = team2IsMixed ? (team2Players[1].mixed_elo_rating || 1200) : (team2Players[1].levels_elo_rating || 1200);
  const allElos = [team1Player1Elo, team1Player2Elo, team2Player1Elo, team2Player2Elo];
  const eloRange = Math.max(...allElos) - Math.min(...allElos);
  const crossCourtBalance = Math.max(0, MATCHMAKING_CONFIG.elo.crossCourtBase - (eloRange * MATCHMAKING_CONFIG.elo.crossCourtMultiplier));
  const individualBalance = Math.max(0, MATCHMAKING_CONFIG.elo.individualBase - (eloRange * MATCHMAKING_CONFIG.elo.individualMultiplier));
  const totalScore = teamBalance + individualBalance + crossCourtBalance;
  if (Math.abs(teamBalanceClamped) > 100 || Math.abs(individualBalance) > 100) {
    debugLog('⚖️ ELO imbalance detected:', {
      team1: team1Players.map(p => `${p.name}(${team1IsMixed ? p.mixed_elo_rating : p.levels_elo_rating})`),
      team2: team2Players.map(p => `${p.name}(${team2IsMixed ? p.mixed_elo_rating : p.levels_elo_rating})`),
      team1AvgElo: team1EloAvg.toFixed(2),
      team2AvgElo: team2EloAvg.toFixed(2),
      teamEloDiff: teamEloDiff.toFixed(2),
      teamBalance: Math.round(teamBalanceClamped),
      totalScore: totalScore.toFixed(2),
    });
  }
  return totalScore;
}

function getDoublesMatchTypeFlags(team1Players, team2Players, playerWaitMetrics = {}, playersById = null) {
  const gameType = detectGameType(team1Players, team2Players);
  const pb = playersById || Object.fromEntries(
    [...team1Players, ...team2Players].filter((p) => p?.id).map((p) => [p.id, p]),
  );
  const t1Ids = team1Players.map((p) => p?.id).filter(Boolean);
  const t2Ids = team2Players.map((p) => p?.id).filter(Boolean);
  const isSessionMensMatch = t1Ids.length === 2 && t2Ids.length === 2
    && isSessionMensDoublesEloExemptLineup(t1Ids, t2Ids, playerWaitMetrics, pb);
  return {
    gameType,
    isMixedMatch: gameType.id === 1,
    isMensMatch: gameType.id === 2 || isSessionMensMatch,
    isWomensMatch: gameType.id === 3,
    isSessionMensMatch,
  };
}

function isPlayerMatchPreferenceViolated(player, waitMetrics, flags, playerWaitMetrics = null) {
  const pwm = playerWaitMetrics || {};
  const preference = waitMetrics.matchPreference || 'no_preference';
  if (preference === 'no_preference') {
    return false;
  }
  if (preference === 'prefer_mixed') {
    if (isMensDoublesEligible(player?.id, pwm) && flags.isSessionMensMatch) {
      return false;
    }
    return !flags.isMixedMatch;
  }
  if (preference === 'prefer_levels') {
    const playerGender = player.gender?.toLowerCase();
    if (playerGender === 'male') {
      return !flags.isMensMatch;
    }
    if (playerGender === 'female') {
      if (isMensDoublesEligible(player?.id, pwm) && flags.isSessionMensMatch) {
        return false;
      }
      return !flags.isWomensMatch;
    }
  }
  return false;
}

/**
 * Hard-block combinations that violate a player's hard match preference.
 * Never relaxed by the progressive relaxation ladder.
 *
 * @returns {number} 100 if allowed, -Infinity if blocked
 */
function checkHardMatchPreferenceBlock(team1, team2, playersById, playerWaitMetrics = {}) {
  if (!playerWaitMetrics || Object.keys(playerWaitMetrics).length === 0) {
    return 100;
  }

  const team1Players = team1.map(id => playersById[id]).filter(Boolean);
  const team2Players = team2.map(id => playersById[id]).filter(Boolean);
  if (team1Players.length !== 2 || team2Players.length !== 2) {
    return 100;
  }

  const flags = getDoublesMatchTypeFlags(team1Players, team2Players, playerWaitMetrics, playersById);
  for (const playerId of [...team1, ...team2]) {
    const player = playersById[playerId];
    const waitMetrics = playerWaitMetrics[playerId];
    if (!player || !waitMetrics) {
      continue;
    }
    if ((waitMetrics.preferenceStrength || 'soft') !== 'hard') {
      continue;
    }
    if (isPlayerMatchPreferenceViolated(player, waitMetrics, flags, playerWaitMetrics)) {
      debugLog('🚫 BLOCK [HARD PREFERENCE]: Player cannot be placed in this game type', {
        player: player.name || playerId,
        preference: waitMetrics.matchPreference,
        gameType: flags.gameType?.name,
      });
      return -Infinity;
    }
  }
  return 100;
}

/**
 * Score player match preferences
 * Soft violations result in large scoring penalties; hard violations are blocked separately.
 *
 * @param {Array} team1 - Team 1 player IDs
 * @param {Array} team2 - Team 2 player IDs
 * @param {Object} playersById - Map of player objects by ID
 * @param {Object} playerWaitMetrics - Wait metrics including match_preference
 * @returns {number} Penalty score (negative for soft violations)
 */
function scorePlayerPreferences(team1, team2, playersById, playerWaitMetrics = {}) {
  if (!playerWaitMetrics || Object.keys(playerWaitMetrics).length === 0) {
    return 0;
  }

  const team1Players = team1.map(id => playersById[id]).filter(Boolean);
  const team2Players = team2.map(id => playersById[id]).filter(Boolean);
  if (team1Players.length !== 2 || team2Players.length !== 2) {
    return 0;
  }

  const flags = getDoublesMatchTypeFlags(team1Players, team2Players, playerWaitMetrics, playersById);
  let totalPenalty = 0;
  const violationPenalty = MATCHMAKING_CONFIG.preferences.violationPenalty;

  for (const playerId of [...team1, ...team2]) {
    const player = playersById[playerId];
    const waitMetrics = playerWaitMetrics[playerId];
    if (!player || !waitMetrics) {
      continue;
    }
    if ((waitMetrics.preferenceStrength || 'soft') === 'hard') {
      continue;
    }
    if (isPlayerMatchPreferenceViolated(player, waitMetrics, flags, playerWaitMetrics)) {
      totalPenalty -= violationPenalty;
      debugLog('🎯 Soft preference violation:', {
        player: player.name,
        preference: waitMetrics.matchPreference,
        gameType: flags.gameType?.name,
        penalty: -violationPenalty,
      });
    }
  }

  return totalPenalty;
}

function calculateSameTeamPenalty(team, sessionState, playersById, partnerships = [], relaxationLevel = RELAXATION_LEVELS.STRICT) {
  if (!sessionState.playerInteractions || team.length !== 2) return 0;
  const [p1, p2] = team;
  
  // Use unified interaction penalty system
  const penaltyResult = calculateUnifiedInteractionPenalties(p1, p2, sessionState, partnerships, true);
  
  // Use threshold values directly - no fixed penalty or relaxation needed
  // High penalties (like -30,000) are not hard blocks, so the system can still select them if needed
  const totalPenalty = penaltyResult.sameTeamPenalty + penaltyResult.totalPenalty;
  return -totalPenalty;
}

function calculateOpponentPenalty(team1, team2, sessionState, playersById, partnerships = [], relaxationLevel = RELAXATION_LEVELS.STRICT) {
  if (!sessionState.playerInteractions || team1.length !== 2 || team2.length !== 2) return 0;
  
  // Check if any players are in partnerships - if so, exempt them from opponent penalties
  // (Partnerships are forced to play together, so they shouldn't be penalized for playing against each other)
  const partnershipMap = buildPartnershipMap(partnerships);
  const hasPartnershipInGame = [...team1, ...team2].some(playerId => {
    const partnerId = partnershipMap.get(playerId);
    return partnerId && ([...team1, ...team2].includes(partnerId));
  });
  
  let totalPenalty = 0;
  team1.forEach(p1 => {
    team2.forEach(p2 => {
      // Skip penalty if these players are partners (forced to be together)
      const p1Partner = partnershipMap.get(p1);
      const p2Partner = partnershipMap.get(p2);
      if (p1Partner === p2 || p2Partner === p1) {
        return; // No penalty for partners playing against each other
      }
      
      // Use unified interaction penalty system (isSameTeam = false for opponents)
      const penaltyResult = calculateUnifiedInteractionPenalties(p1, p2, sessionState, partnerships, false);
      
      // Use threshold values directly - no fixed penalty or relaxation needed
      // High penalties (like -30,000) are not hard blocks, so the system can still select them if needed
      // Only total penalty applies for opponents (no same-team penalty)
      totalPenalty += -penaltyResult.totalPenalty;
    });
  });
  return totalPenalty;
}

function scorePlayerInteractions(team1, team2, sessionState, playersById, partnerships = [], relaxationLevel = RELAXATION_LEVELS.STRICT) {
  const sameTeamPenalty = calculateSameTeamPenalty(team1, sessionState, playersById, partnerships, relaxationLevel) +
    calculateSameTeamPenalty(team2, sessionState, playersById, partnerships, relaxationLevel);
  const opponentPenalty = calculateOpponentPenalty(team1, team2, sessionState, playersById, partnerships, relaxationLevel);
  return sameTeamPenalty + opponentPenalty;
}

// Block logging counters (reset periodically to avoid memory buildup)
const blockLogCounters = {
  gameType: 0,
  minPlus2: 0,
  partnershipMinPlus2: 0,
  partnershipSplit: 0,
  repeatTeam: 0,
  categoryGap: 0,
  consecutiveInteraction: 0,
  maxLogsPerType: 3,
  reset() {
    Object.keys(this).forEach(key => {
      if (key !== 'maxLogsPerType' && key !== 'reset') {
        this[key] = 0;
      }
    });
  }
};

function scoreCombination(split, playersById, partnerships, sessionState, relaxationLevel = RELAXATION_LEVELS.STRICT, scheduledCombinations = [], gameTypeRestriction = 'all_types', playerCategories = null, gamesMin = null, scheduledGames = [], playersPassedCooldown = null, expandedThreshold = null) {
  const { team1, team2 } = split;
  let score = 0;
  const team1Objects = team1.map(id => playersById[id]);
  const team2Objects = team2.map(id => playersById[id]);
  debugLog('🔎 scoreCombination called:', {
    team1: team1Objects.map(p => `${p?.name}(${p?.gender})`),
    team2: team2Objects.map(p => `${p?.name}(${p?.gender})`),
    gameTypeRestriction,
    hasUndefinedPlayers: team1Objects.some(p => !p) || team2Objects.some(p => !p),
  });
  const _sePlayerWaitMetrics = sessionState?.playerWaitMetrics || {};
  if (!isGameTypeAllowed(team1Objects, team2Objects, gameTypeRestriction, _sePlayerWaitMetrics)) {
    if (blockLogCounters.gameType < blockLogCounters.maxLogsPerType) {
    const team1Genders = team1Objects.map(p => p?.gender || 'unknown').join(', ');
    const team2Genders = team2Objects.map(p => p?.gender || 'unknown').join(', ');
      const team1Mixed = isMixed(team1Objects);
      const team2Mixed = isMixed(team2Objects);
      debugLog('🚫 BLOCK [GAME TYPE]: Returning -Infinity due to game type restriction', {
      restriction: gameTypeRestriction,
        team1: team1Objects.map(p => `${p?.name || 'Unknown'}(${p?.gender?.charAt(0) || '?'})`).join(' + '),
        team2: team2Objects.map(p => `${p?.name || 'Unknown'}(${p?.gender?.charAt(0) || '?'})`).join(' + '),
        team1Mixed,
        team2Mixed,
        blocked: `T1(${team1Mixed ? 'mixed' : 'same'}) vs T2(${team2Mixed ? 'mixed' : 'same'})`,
    });
      blockLogCounters.gameType++;
    }
    return -Infinity;
  }
  if (gamesMin !== null && gamesMin !== undefined) {
    const allPlayerIds = [...team1, ...team2];
    let wouldBlock = false;
    const blockingPlayers = [];
    const effectiveMinGames = expandedThreshold !== null && expandedThreshold !== undefined ? expandedThreshold : gamesMin;
    for (const id of allPlayerIds) {
      if (wouldReachMinPlus2(id, playersById, effectiveMinGames)) {
        wouldBlock = true;
        blockingPlayers.push(id);
      }
    }
    if (wouldBlock) {
      const wouldBeFair = wouldBeFairAfterGame(team1, team2, playersById, gamesMin);
      if (!wouldBeFair) {
        if (blockLogCounters.minPlus2 < blockLogCounters.maxLogsPerType) {
          debugLog('🚫 BLOCK [MIN+2]: Blocking combination - player would reach min+2 and game would NOT be fair after', {
            team1: team1.map(id => playersById[id]?.name || id.slice(0, 8)).join(' + '),
            team2: team2.map(id => playersById[id]?.name || id.slice(0, 8)).join(' + '),
            blockingPlayers: blockingPlayers.map(id => playersById[id]?.name || id.slice(0, 8)),
          });
          blockLogCounters.minPlus2++;
        }
        return -Infinity;
      }
      debugLog('✅ Fairness override: Allowing player despite min+2 - game would be fair after completion', {
        blockingPlayers: blockingPlayers.map(id => playersById[id]?.name || id.slice(0, 8)),
      });
    }
  }
  const partnershipMap = buildPartnershipMap(partnerships);
  for (const id of [...team1, ...team2]) {
    const partnerId = partnershipMap.get(id);
    if (partnerId && ([...team1, ...team2].includes(partnerId))) {
      if (gamesMin !== null && !canPartnershipPlay(id, partnerId, playersById, gamesMin)) {
        // Partnership would violate min+2, but check if game would be fair afterward
        // (Same logic as individual player min+2 override)
        const wouldBeFair = wouldBeFairAfterGame(team1, team2, playersById, gamesMin);
        if (!wouldBeFair) {
          if (blockLogCounters.partnershipMinPlus2 < blockLogCounters.maxLogsPerType) {
            debugLog('🚫 BLOCK [PARTNERSHIP MIN+2]: Blocking partnership - would create min+2 and game would NOT be fair after', {
              team1: team1.map(id => playersById[id]?.name || id.slice(0, 8)).join(' + '),
              team2: team2.map(id => playersById[id]?.name || id.slice(0, 8)).join(' + '),
              partnership: `${playersById[id]?.name || id.slice(0, 8)} + ${playersById[partnerId]?.name || partnerId.slice(0, 8)}`,
            });
            blockLogCounters.partnershipMinPlus2++;
          }
        return -Infinity;
        }
        // Allow partnership to play despite min+2 because game would be fair afterward
        debugLog('✅ Fairness override: Allowing partnership despite min+2 - game would be fair after completion', {
          partnership: `${playersById[id]?.name || id.slice(0, 8)} + ${playersById[partnerId]?.name || partnerId.slice(0, 8)}`,
        });
      }
    }
  }
  if (gamesMin !== null) {
    const allPlayerIds = [...team1, ...team2];
    let fairnessScore = 0;
    let totalGamesFromMin = 0;
    allPlayerIds.forEach(id => {
      const player = playersById[id];
      const effectiveGames = player?.effectiveGamesPlayed || player?.gamesPlayedThisSession || 0;
      const gamesAboveMin = effectiveGames - gamesMin;
      totalGamesFromMin += gamesAboveMin;
      if (effectiveGames === gamesMin) {
        fairnessScore += MATCHMAKING_CONFIG.fairness.minBonus;
      } else if (gamesAboveMin === 1) {
        fairnessScore += MATCHMAKING_CONFIG.fairness.minPlusOneBonus;
      } else if (gamesAboveMin === 2) {
        fairnessScore += MATCHMAKING_CONFIG.fairness.minPlusTwoBonus;
      }
    });
    fairnessScore -= MATCHMAKING_CONFIG.fairness.gamesAboveMinPenalty * totalGamesFromMin;
    score += fairnessScore;
    if (fairnessScore !== 0) {
      debugLog('⚖️ Fairness scoring (small):', {
        totalGamesFromMin,
        fairnessScore: fairnessScore.toFixed(0),
      });
    }
  }
  const team1PartnershipObjects = team1.map(id => ({ id, name: playersById[id]?.name }));
  const team2PartnershipObjects = team2.map(id => ({ id, name: playersById[id]?.name }));
  debugLog('🔍 Checking partnership violations:', {
    team1: team1PartnershipObjects.map(p => `${p.name}(${p.id})`),
    team2: team2PartnershipObjects.map(p => `${p.name}(${p.id})`),
    partnershipsCount: partnerships?.length || 0,
    partnerships: partnerships?.map(p => ({
      p1: p.player1Id || p.player1_id,
      p2: p.player2Id || p.player2_id,
      p1Name: playersById[p.player1Id || p.player1_id]?.name,
      p2Name: playersById[p.player2Id || p.player2_id]?.name
    })) || []
  });
  const partnershipViolation = checkPartnershipViolations(team1PartnershipObjects, team2PartnershipObjects, partnerships);
  if (partnershipViolation) {
    // Partnerships are always respected - never allow splitting
    if (blockLogCounters.partnershipSplit < blockLogCounters.maxLogsPerType) {
      debugLog('🚫 BLOCK [PARTNERSHIP SPLIT]: Partners must always play together', {
        team1: team1.map(id => playersById[id]?.name || id.slice(0, 8)).join(' + '),
        team2: team2.map(id => playersById[id]?.name || id.slice(0, 8)).join(' + '),
        partnerships: partnerships?.map(p => {
          const p1 = p.player1Id || p.player1_id;
          const p2 = p.player2Id || p.player2_id;
          return `${playersById[p1]?.name || p1.slice(0, 8)} + ${playersById[p2]?.name || p2.slice(0, 8)}`;
        }).join(', ') || 'none',
      });
      blockLogCounters.partnershipSplit++;
    }
    return -Infinity;
  }
  if (sessionState?.matchPreferencesEnabled !== false) {
    const hardPrefCheck = checkHardMatchPreferenceBlock(
      team1,
      team2,
      playersById,
      sessionState?.playerWaitMetrics || {},
    );
    if (hardPrefCheck === -Infinity) {
      return -Infinity;
    }
  }
  if (sessionState?.playerInteractions) {
    const isPartnership = partnerships.some(p => {
      const p1 = p.player1Id || p.player1_id;
      const p2 = p.player2Id || p.player2_id;
      return (team1.includes(p1) && team1.includes(p2)) ||
        (team2.includes(p1) && team2.includes(p2));
    });
    const isScheduled = scheduledCombinations.some(scheduledCombination =>
      team1.every(id => scheduledCombination.includes(id)) &&
      team2.every(id => scheduledCombination.includes(id))
    );
    if (!isPartnership && !isScheduled) {
      const team1JustPlayed = getSameTeamInteractionCount(team1[0], team1[1], sessionState);
      const team2JustPlayed = getSameTeamInteractionCount(team2[0], team2[1], sessionState);
      if ((team1JustPlayed > 0 || team2JustPlayed > 0) && relaxationLevel < RELAXATION_LEVELS.ALLOW_REPEAT_TEAMS) {
        if (blockLogCounters.repeatTeam < blockLogCounters.maxLogsPerType) {
          debugLog('🚫 BLOCK [REPEAT TEAM]: Players just played together on same team', {
            team1: team1.map(id => playersById[id]?.name || id.slice(0, 8)).join(' + '),
            team2: team2.map(id => playersById[id]?.name || id.slice(0, 8)).join(' + '),
          team1JustPlayed,
          team2JustPlayed,
          relaxationLevel,
        });
          blockLogCounters.repeatTeam++;
        }
        return -Infinity;
      } else if ((team1JustPlayed > 0 || team2JustPlayed > 0) && relaxationLevel >= RELAXATION_LEVELS.ALLOW_REPEAT_TEAMS) {
        const repeatPenalty = -MATCHMAKING_CONFIG.penalties.repeatTeamBase * (team1JustPlayed + team2JustPlayed);
        score += repeatPenalty;
        debugLog('⚠️ Relaxed: Allowing repeat team with heavy penalty', {
          team1: team1.map(id => playersById[id]?.name || id),
          team2: team2.map(id => playersById[id]?.name || id),
          repeatPenalty,
        });
      }
    }
  }
  score += scoreGameTypeBalance(team1, team2, playersById);
  score += getUnbalancedGamePenalty(team1Objects, team2Objects, gameTypeRestriction);
  
  // Note: Scheduled games no longer receive any bonus - they compete fairly with all other combinations
  // They are identified in the pool but not given preferential scoring
  
  const playersAtMin = gamesMin !== null ? [...team1, ...team2].filter(id => {
    const player = playersById[id];
    const effectiveGames = player?.effectiveGamesPlayed || player?.gamesPlayedThisSession || 0;
    return effectiveGames === gamesMin;
  }) : [];
  const hasPlayersAtMin = playersAtMin.length > 0;
  const fairnessContext = {
    allowFairnessOverride: hasPlayersAtMin,
    playersAtMinCount: playersAtMin.length,
    gamesMin,
  };
  const categoryCheck = evaluateCategoryMatch(team1Objects, team2Objects, playerCategories, fairnessContext);
  // Check for hard block (gap 3+)
  if (!categoryCheck.allowed) {
    if (blockLogCounters.categoryGap < blockLogCounters.maxLogsPerType) {
      debugLog('🚫 BLOCK [CATEGORY GAP]: Category gap >= 3', {
        team1: team1.map(id => playersById[id]?.name || id.slice(0, 8)).join(' + '),
        team2: team2.map(id => playersById[id]?.name || id.slice(0, 8)).join(' + '),
        gap: categoryCheck.gap,
      });
      blockLogCounters.categoryGap++;
    }
    return -Infinity;
  }
  // Apply penalty for gaps 1 and 2
  score += categoryCheck.penalty;
  if (categoryCheck.gap > 0) {
    debugLog(`⚖️ Applied category gap penalty: ${categoryCheck.penalty} for gap ${categoryCheck.gap}`);
  }
  
  // Check for consecutive interaction hard block
  const consecutiveCheck = checkConsecutiveInteractionBlock(team1, team2, sessionState, partnerships);
  if (consecutiveCheck.shouldBlock) {
    const [p1, p2] = consecutiveCheck.blockingPair;
    if (blockLogCounters.consecutiveInteraction < blockLogCounters.maxLogsPerType) {
      debugLog('🚫 BLOCK [CONSECUTIVE INTERACTION]: Threshold exceeded', {
        team1: team1.map(id => playersById[id]?.name || id.slice(0, 8)).join(' + '),
        team2: team2.map(id => playersById[id]?.name || id.slice(0, 8)).join(' + '),
        blockingPair: `${playersById[p1]?.name || p1.slice(0, 8)} + ${playersById[p2]?.name || p2.slice(0, 8)}`,
      consecutiveCount: consecutiveCheck.maxConsecutive,
      threshold: MATCHMAKING_CONFIG.consecutiveInteractionBlock.threshold,
    });
      blockLogCounters.consecutiveInteraction++;
    }
    return -Infinity;
  }
  
  const eloBalance = scoreELOBalance(team1, team2, playersById);
  score += eloBalance;
  const interactionPenalty = scorePlayerInteractions(team1, team2, sessionState, playersById, partnerships, relaxationLevel);
  score += interactionPenalty;
  
  // Apply player preference penalties
  const playerWaitMetrics = sessionState?.playerWaitMetrics || {};
  let preferencePenalty = 0;
  if (sessionState?.matchPreferencesEnabled !== false) {
    preferencePenalty = scorePlayerPreferences(team1, team2, playersById, playerWaitMetrics);
  }
  score += preferencePenalty;
  
  if (playersPassedCooldown && playersPassedCooldown.size > 0) {
    const allPlayerIds = [...team1, ...team2];
    let cooldownBonus = 0;
    let playersPassedCount = 0;
    allPlayerIds.forEach(playerId => {
      if (playersPassedCooldown.has(playerId)) {
        playersPassedCount++;
        cooldownBonus += MATCHMAKING_CONFIG.bonuses.cooldownPerPlayer;
      }
    });
    if (cooldownBonus > 0) {
      score += cooldownBonus;
      debugLog('❄️ Cooldown bonus:', {
        playersPassedCount,
        cooldownBonus: cooldownBonus.toFixed(0),
      });
    }
  } else if (sessionState?.cooldownEnabled === false) {
    const allPlayerIds = [...team1, ...team2];
    let cooldownPenalty = 0;
    let onCooldownCount = 0;
    allPlayerIds.forEach((playerId) => {
      if (playerWaitMetrics[playerId]?.isOnCooldown) {
        onCooldownCount++;
        cooldownPenalty += MATCHMAKING_CONFIG.bonuses.cooldownPerPlayer;
      }
    });
    if (cooldownPenalty > 0) {
      score -= cooldownPenalty;
      debugLog('❄️ Cooldown penalty (soft mode):', {
        onCooldownCount,
        cooldownPenalty: cooldownPenalty.toFixed(0),
      });
    }
  }
  debugLog('📊 Final score breakdown:', {
    team1: team1.map(id => playersById[id]?.name || id),
    team2: team2.map(id => playersById[id]?.name || id),
    totalScore: score.toFixed(0),
    eloBalance: eloBalance.toFixed(0),
    interactionPenalty: interactionPenalty.toFixed(0),
  });
  return score;
}

function havePlayedTogether(team1, team2, matchHistory) {
  if (!matchHistory || !Array.isArray(matchHistory) || matchHistory.length === 0) return false;
  const team1Ids = new Set(team1.map(p => p.id));
  const team2Ids = new Set(team2.map(p => p.id));
  debugLog('🔍 havePlayedTogether called with:', {
    team1Ids: Array.from(team1Ids),
    team2Ids: Array.from(team2Ids),
    matchHistoryLength: matchHistory.length,
    firstGame: matchHistory[0],
  });
  for (const game of matchHistory) {
    if (!game) {
      debugLog('🔍 Skipping null/undefined game');
      continue;
    }
    
    // Support both old format (teams: [team1, team2]) and new format (team1, team2)
    let gameTeam1, gameTeam2;
    if (game.teams && Array.isArray(game.teams) && game.teams.length >= 2) {
      gameTeam1 = game.teams[0];
      gameTeam2 = game.teams[1];
    } else if (game.team1 && game.team2) {
      gameTeam1 = game.team1;
      gameTeam2 = game.team2;
    } else {
      debugLog('🔍 Skipping malformed game:', game);
      continue;
    }
    
    const gameTeam1Ids = new Set(gameTeam1);
    const gameTeam2Ids = new Set(gameTeam2);
    if (gameTeam1Ids.size === team1Ids.size &&
      [...gameTeam1Ids].every(id => team1Ids.has(id))) {
      return true;
    }
    if (gameTeam2Ids.size === team1Ids.size &&
      [...gameTeam2Ids].every(id => team1Ids.has(id))) {
      return true;
    }
    const team1InGame = [...team1Ids].some(id => gameTeam1Ids.has(id) || gameTeam2Ids.has(id));
    const team2InGame = [...team2Ids].some(id => gameTeam1Ids.has(id) || gameTeam2Ids.has(id));
    if (team1InGame && team2InGame) {
      return true;
    }
  }
  return false;
}

function getTeamElo(team) {
  return team.reduce((sum, p) => {
    const isTeamMixed = isMixed(team);
    const elo = isTeamMixed ? (p.mixed_elo_rating || 1200) : (p.levels_elo_rating || 1200);
    return sum + elo;
  }, 0);
}

/**
 * Check for partnership violations (safety net)
 * Since partnerships are now units, violations should never occur.
 * This function serves as a safety check in case something goes wrong.
 * @param {Array} team1 - Team 1 player IDs or objects
 * @param {Array} team2 - Team 2 player IDs or objects
 * @param {Array} partnerships - Array of partnership objects
 * @returns {boolean} True if violation detected (should never happen with units)
 */
function checkPartnershipViolations(team1, team2, partnerships = []) {
  if (!partnerships || partnerships.length === 0) return false;
  
  // Extract player IDs from teams (handle both ID strings and player objects)
  const team1Ids = new Set(team1.map(p => typeof p === 'string' ? p : (p.id || p)));
  const team2Ids = new Set(team2.map(p => typeof p === 'string' ? p : (p.id || p)));
  const allPlayerIdsInGame = new Set([...team1Ids, ...team2Ids]);

  // Check each partnership
  for (const partnership of partnerships) {
    const partner1Id = partnership.player1Id || partnership.player1_id;
    const partner2Id = partnership.player2Id || partnership.player2_id;

    // Skip invalid partnerships (null/undefined values)
    if (!partner1Id || !partner2Id) {
      continue;
    }

    const partner1InGame = allPlayerIdsInGame.has(partner1Id);
    const partner2InGame = allPlayerIdsInGame.has(partner2Id);

    // If only one partner is in the game, this violates the partnership requirement
    // (Should never happen since partnerships are units)
    if ((partner1InGame && !partner2InGame) || (!partner1InGame && partner2InGame)) {
      console.error('🚫 SAFETY NET: Partnership violation detected - only one partner in game', {
        partner1Id,
        partner2Id,
        partner1InGame,
        partner2InGame,
      });
      return true; // Violation detected
    }

    // If both partners are in the game but on different teams, that's also a violation
    // (Should never happen since partnerships are units and team splits respect partnerships)
    if (partner1InGame && partner2InGame) {
      const partner1InTeam1 = team1Ids.has(partner1Id);
      const partner2InTeam1 = team1Ids.has(partner2Id);
      const partner1InTeam2 = team2Ids.has(partner1Id);
      const partner2InTeam2 = team2Ids.has(partner2Id);

      if ((partner1InTeam1 && partner2InTeam2) || (partner1InTeam2 && partner2InTeam1)) {
        console.error('🚫 SAFETY NET: Partnership violation detected - partners on different teams', {
          partner1Id,
          partner2Id,
        });
        return true; // Violation detected
      }
    }
  }
  
  return false; // No violations (expected behavior with units)
}

function resetBlockLogCounters() {
  blockLogCounters.reset();
}

/**
 * Wrapper for scoreCombination that supports both legacy and normalized scoring
 * Based on feature flags in MATCHMAKING_CONFIG, this will:
 * - Use legacy scoring (default)
 * - Use normalized scoring (if useNormalizedScoring = true)
 * - Compare both systems (if compareScoringSystems = true)
 * 
 * @param {Object} split - args[0]
 * @param {Object} playersById - args[1]
 * @param {Array} partnerships - args[2]
 * @param {Object} sessionState - args[3]
 * @param {number} relaxationLevel - args[4]
 * @param {Array} scheduledCombinations - args[5]
 * @param {string} gameTypeRestriction - args[6]
 * @param {Object} playerCategories - args[7]
 * @param {number} gamesMin - args[8]
 * @param {Array} scheduledGames - args[9]
 * @param {Set} playersPassedCooldown - args[10]
 * @param {number} expandedThreshold - args[11]
 * @param {Object} hardBlocks - args[12] - NEW: Hard block overrides from relaxation system
 * @param {boolean} cooldownEnabled - args[13] - NEW: Session-level cooldown setting (default true)
 */
function scoreCombinationWithFeatureFlags(...args) {
  const useNormalized = shouldUseNormalizedScoring();
  const shouldCompare = shouldCompareScoringSystems();
  
  // Extract new parameters (backward compatible - default if not provided)
  const hardBlocks = args[12] || null;
  const cooldownEnabled = args[13] !== undefined ? args[13] : false;
  const poolPlayerIds = args[15] || null;
  const scoringConfigForNormalized =
    args.length > 14 && args[14] !== undefined && args[14] !== null
      ? args[14]
      : getScoringConfig();
  
  if (shouldCompare) {
    // A/B testing mode - run both and log comparison
    const comparison = compareScoringSystems(scoreCombination, {
      split: args[0],
      playersById: args[1],
      partnerships: args[2],
      sessionState: args[3],
      relaxationLevel: args[4],
      scheduledCombinations: args[5],
      gameTypeRestriction: args[6],
      playerCategories: args[7],
      gamesMin: args[8],
      scheduledGames: args[9],
      playersPassedCooldown: args[10],
      expandedThreshold: args[11],
      scoringConfig: scoringConfigForNormalized,
      hardBlocks,
      cooldownEnabled,
    });
    
    // Log comparison for analysis
    if (comparison.agreement.disagreement) {
      console.warn('⚠️ SCORING DISAGREEMENT:', comparison);
    } else if (MATCHMAKING_CONFIG.debugLogs) {
      debugLog('📊 Scoring Comparison:', {
        matchup: comparison.matchup,
        legacy: comparison.legacy.score,
        normalized: comparison.normalized.score,
        breakdown: comparison.normalized.breakdown,
      });
    }
    
    // Return the score based on which system is active
    return useNormalized ? comparison.normalized.score : comparison.legacy.score;
  }
  
  if (useNormalized) {
    // Use new normalized scoring system
    const result = scoreCombinationNormalized(
      args[0],  // split
      args[1],  // playersById
      args[2],  // partnerships
      args[3],  // sessionState
      args[4],  // relaxationLevel
      args[5],  // scheduledCombinations
      args[6],  // gameTypeRestriction
      args[7],  // playerCategories
      args[8],  // gamesMin
      args[9],  // scheduledGames
      args[10], // playersPassedCooldown
      args[11], // expandedThreshold
      scoringConfigForNormalized,
      hardBlocks,      // NEW: Pass hardBlocks
      cooldownEnabled,  // NEW: Pass cooldownEnabled
      poolPlayerIds,
    );
    
    return result.score;
  }
  
  // Default: use legacy scoring (doesn't use hardBlocks - uses old relaxationLevel)
  return scoreCombination(...args);
}

export {
  buildPartnershipMap,
  getSameTeamInteractionCount,
  getTeamElo,
  havePlayedTogether,
  isGameTypeAllowed,
  isUnfairGenderMatchup,
  scoreCombination,
  scoreCombinationWithFeatureFlags,  // New wrapper function
  scoreELOBalance,
  scoreGameTypeBalance,
  scorePlayerInteractions,
  scorePlayerPreferences,
  checkHardMatchPreferenceBlock,
  getUnbalancedGamePenalty,
  evaluateCategoryMatch,
  isMixed,
  getCategoryKeyForGame,
  checkPartnershipViolations,
  resetBlockLogCounters,
};

